import React, { useEffect, useState, useRef } from "react";
import Annotator from "@amnstak/react-image-annotate";
import "./AnnotateModal.css";
import ClipLoader from "react-spinners/ClipLoader";

const AnnotateModal = ({ show, imageSrc, imageName, regionClsList, regions = [], onClose, onSave }) => {
    const [annotatorState, setAnnotatorState] = useState(null);
    const focusTimeoutRef = useRef(null);
    const [isImageLoading, setIsImageLoading] = useState(true);

    useEffect(() => {
        if (!show || !imageSrc) return;

        setIsImageLoading(true);

        const img = new Image();
        img.src = imageSrc;

        if (img.complete) {
            setIsImageLoading(false);
            return;
        }

        img.onload = () => setIsImageLoading(false);
        img.onerror = () => setIsImageLoading(false);

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [show, imageSrc]);

    useEffect(() => {
        if (!show) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [show, onClose]);

    useEffect(() => {
        if (!show) return;

        const observer = new MutationObserver(() => {
            const input = document.querySelector('input[id^="react-select"]');
            if (input && document.activeElement !== input) {
                clearTimeout(focusTimeoutRef.current);
                focusTimeoutRef.current = setTimeout(() => {
                    input.focus();
                }, 50);
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            clearTimeout(focusTimeoutRef.current);
        };
    }, [show]);

    if (!show) return null;

    const normalizedRegions = (regions || []).map(region => {
        if (region.type === "polygon" && region.pointsX && region.pointsY) {
            return {
                ...region,
                points: region.pointsX.map((x, i) => [x, region.pointsY[i]]),
            };
        }
        return region;
    });

    const createAnnotatedImage = async (originalImageSrc, regions) => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();
            img.crossOrigin = "anonymous";

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;

                ctx.drawImage(img, 0, 0);

                regions.forEach((region) => {
                    if (region.type === "box") {
                        const x = region.x * img.width;
                        const y = region.y * img.height;
                        const width = region.w * img.width;
                        const height = region.h * img.height;

                        ctx.strokeStyle = region.color || "#00ff00";
                        ctx.lineWidth = Math.max(2, img.width * 0.002);
                        ctx.strokeRect(x, y, width, height);

                        if (region.cls) {
                            const fontSize = Math.max(16, img.height * 0.02);
                            ctx.font = `${fontSize}px sans-serif`;
                            ctx.textBaseline = "top";

                            const label = region.cls;
                            const textWidth = ctx.measureText(label).width;
                            const padding = 4;

                            ctx.fillStyle = region.color || "#00ff00";
                            ctx.fillRect(x, y - fontSize - padding * 2, textWidth + padding * 2, fontSize + padding * 2);

                            ctx.fillStyle = "#ffffff";
                            ctx.fillText(label, x + padding, y - fontSize - padding);
                        }
                    }

                    if (region.type === "point") {
                        const x = region.x * img.width;
                        const y = region.y * img.height;

                        ctx.fillStyle = region.color || "#00ff00";
                        ctx.beginPath();
                        ctx.arc(x, y, 4, 0, 2 * Math.PI);
                        ctx.fill();
                    }

                    if (region.type === "polygon") {
                        if (!region.points || region.points.length < 2) return;

                        ctx.strokeStyle = region.color || "#ff0000";
                        ctx.lineWidth = Math.max(2, img.width * 0.002);
                        ctx.beginPath();

                        const firstPoint = region.points[0];
                        const firstX = Array.isArray(firstPoint) ? firstPoint[0] : firstPoint.x;
                        const firstY = Array.isArray(firstPoint) ? firstPoint[1] : firstPoint.y;
                        ctx.moveTo(firstX * img.width, firstY * img.height);

                        for (let i = 1; i < region.points.length; i++) {
                            const p = region.points[i];
                            const x = Array.isArray(p) ? p[0] : p.x;
                            const y = Array.isArray(p) ? p[1] : p.y;
                            ctx.lineTo(x * img.width, y * img.height);
                        }

                        ctx.closePath();
                        ctx.stroke();

                        if (region.cls) {
                            let minX = Math.min(...region.points.map(p => Array.isArray(p) ? p[0] : p.x)) * img.width;
                            let minY = Math.min(...region.points.map(p => Array.isArray(p) ? p[1] : p.y)) * img.height;

                            const fontSize = Math.max(16, img.height * 0.02);
                            ctx.font = `${fontSize}px sans-serif`;
                            ctx.textBaseline = "top";

                            const label = region.cls;
                            const textWidth = ctx.measureText(label).width;
                            const padding = 4;

                            ctx.fillStyle = region.color || "#00ff00";
                            ctx.fillRect(minX, minY - fontSize - padding * 2, textWidth + padding * 2, fontSize + padding * 2);

                            ctx.fillStyle = "#ffffff";
                            ctx.fillText(label, minX + padding, minY - fontSize - padding);
                        }
                    }

                });


                const base64 = canvas.toDataURL("image/png");
                resolve(base64);
            };

            img.onerror = reject;
            img.src = originalImageSrc;
        });
    };

    const handleExit = async (state) => {
        const regions = state.images[0].regions || [];

        try {
            const base64 = await createAnnotatedImage(imageSrc, regions);
            onSave({ regions, base64 });
        } catch (error) {
            console.error("Failed to create annotated image:", error);
            onSave({ regions, base64: null });
        }

        onClose();
    };

    return (
        <div
            className="custom-modal-overlay"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
            }}
        >
            <div
                className="custom-modal-container"
                style={{
                    width: "60vw",
                    height: "90vh",
                    backgroundColor: "#fff",
                    borderRadius: 8,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <div className="custom-modal-header" style={{ flexShrink: 0 }}>
                    <span className="custom-modal-title">Manual Annotation</span>
                    <button className="custom-modal-close" onClick={onClose}>×</button>
                </div>

                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                    {isImageLoading && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0, left: 0, right: 0, bottom: 0,
                                zIndex: 1055,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(0, 0, 0, 0.35)",
                            }}
                        >
                            <ClipLoader color="#b11111" size={50} />
                        </div>
                    )}

                    {imageSrc ? (
                        <Annotator
                            labelImages
                            regionClsList={regionClsList}
                            regionTagList={[]}
                            images={[{
                                src: imageSrc,
                                name: imageName || 'Door Photo',
                                regions: normalizedRegions
                            }]}
                            showTags={true}
                            selectedImage={0}
                            onExit={handleExit}
                            hideHeader={false}
                            hideHeaderText={false}
                            hideNext={true}
                            hidePrev={true}
                            hideClone={true}
                            hideSettings={true}
                            hideFullScreen={true}
                            hideHotkeys={true}
                            enabledTools={[
                                "select",
                                "create-box",
                                "create-polygon",
                            ]}
                        />
                    ) : (
                        <p style={{ textAlign: "center", marginTop: 40 }}>No image available</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnnotateModal;
